import React, { useEffect } from 'react';
import { Sparkles, ChevronLeft, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAIAssistant } from '@/hooks/useAIAssistant';
import { useAIAssistantContext } from '@/contexts/AIAssistantContext';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import ContextBadge from './ContextBadge';
import AIInput from './AIInput';

interface Props {
  isOpen: boolean;
}

export default function AIAssistantPanel({ isOpen }: Props) {
  const { t } = useTranslation();
  const { pageContext } = useAIAssistantContext();

  const {
    userTier,
    isPro,
    hitFreeLimit,
    conversations,
    activeConversationId,
    loadingConversations,
    loadConversations,
    createConversation,
    openConversation,
    deleteConversation,
    backToList,
    messages,
    loadingMessages,
    isStreaming,
    streamingText,
    activeToolCalls,
    sendMessage,
    retryGuardOutage,
    stopStreaming,
    submitFeedback,
  } = useAIAssistant(pageContext);

  // Load conversations when panel opens
  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen, loadConversations]);

  const handleNew = async () => {
    await createConversation();
  };

  // Free tier upgrade CTA
  const UpgradeBanner = () => (
    <div className="mx-3 mb-2 p-3 rounded-lg bg-linear-to-r/srgb from-iris-600/20 to-iris-600/20 border border-iris-500/20">
      <div className="flex items-center gap-2 mb-1.5">
        <Crown size={14} className="text-iris-400" />
        <span className="text-xs font-medium text-white">
          {hitFreeLimit ? t('aiAssistant.dailyLimitReached') : t('aiAssistant.upgradeForPro')}
        </span>
      </div>
      <p className="text-[11px] text-[#a0a0b0] mb-2">
        {hitFreeLimit
          ? 'You\'ve used all your free AI messages. Upgrade for unlimited AI with actions, memory, and more.'
          : 'Get unlimited AI, create posts by voice, analytics, memory, and 10+ AI actions.'}
      </p>
      <a
        href="/dashboard/settings?tab=subscription"
        className="inline-block px-3 py-1.5 text-[11px] font-medium text-[#0C0C12] bg-iris-600 hover:bg-iris-500 rounded-lg transition-colors"
      >
        Upgrade to Pro — $9/mo
      </a>
    </div>
  );

  // Show conversation list or active chat
  if (!activeConversationId) {
    return (
      <div className="flex flex-col flex-1">
        <ContextBadge context={pageContext} isPro={isPro} />

        {/* Quick start area */}
        {conversations.length === 0 && !loadingConversations && (
          <div className="text-center py-8 px-4">
            <Sparkles size={32} className="mx-auto mb-3 text-iris-400" />
            <p className="text-sm text-[#a0a0b0] mb-1">{t('aiAssistant.welcomeMessage')}</p>
            <p className="text-xs text-[#9a9aac]">{t('aiAssistant.welcomeHint')}</p>
          </div>
        )}

        {!isPro && <UpgradeBanner />}

        <ConversationList
          conversations={conversations}
          loading={loadingConversations}
          onSelect={openConversation}
          onNew={handleNew}
          onDelete={deleteConversation}
        />

        {hitFreeLimit ? (
          <div className="p-3 border-t border-white/6">
            <UpgradeBanner />
          </div>
        ) : (
          <AIInput
            onSend={sendMessage}
            isStreaming={isStreaming}
            onStop={stopStreaming}
            isOpen={isOpen}
          />
        )}
      </div>
    );
  }

  // Active conversation view
  const activeConv = conversations.find(c => c.id === activeConversationId);

  return (
    <div className="flex flex-col flex-1">
      {/* Chat header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/6">
        <button
          onClick={backToList}
          className="p-1.5 rounded-lg text-[#8e8e9e] hover:text-white hover:bg-white/5 transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <p className="text-sm text-white truncate flex-1">{activeConv?.title || t('aiAssistant.newChat')}</p>
      </div>

      <ContextBadge context={pageContext} isPro={isPro} />

      <MessageList
        messages={messages}
        loading={loadingMessages}
        isStreaming={isStreaming}
        streamingText={streamingText}
        activeToolCalls={activeToolCalls}
        onFeedback={submitFeedback}
        onRetryGuardOutage={retryGuardOutage}
      />

      {hitFreeLimit ? (
        <div className="p-3 border-t border-white/6">
          <UpgradeBanner />
        </div>
      ) : (
        <AIInput
          onSend={sendMessage}
          isStreaming={isStreaming}
          onStop={stopStreaming}
          isOpen={isOpen}
        />
      )}
    </div>
  );
}
